import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { storeToRefs } from 'pinia';
import cloneDeep from 'lodash/cloneDeep';
import sidebarItems, { getAllSidebarRoutes } from '@/lib/constants/sidebarItems';
import type { SidebarItem } from '@/lib/constants/sidebarItems';
import { useAppStore } from '@/stores/app';
import { useAuthStore } from '@/stores/auth';
import { useRoute } from '@/composables/useNuxtRouter';

// Stores

export default /*#__PURE__*/_defineComponent({
  __name: 'Sidebar',
  setup(__props) {

const appStore = useAppStore();
const authStore = useAuthStore();
const route = useRoute();

const { userInfo } = storeToRefs(authStore);
const { isSidebarOpened } = storeToRefs(appStore);

// Data
const search = ref('');
const openedMenus = ref<string[]>([]);
const menuItems = ref<SidebarItem[]>([]);
const textInput = ref(null);
const showingSubmenu = ref<string | null>(null);
const popupTop = ref(0);

/**
 * Filtra gli elementi della sidebar basandosi sui ruoli utente
 */
const getSidebarItems = (userRoles: string[]): SidebarItem[] => {
	const clonedItems = cloneDeep(sidebarItems) as SidebarItem[];

	return clonedItems.filter((menuItem) => {
		const allowed = userRoles.some((userRole) => menuItem.restrictedTo.includes(userRole as any));

		if (allowed && menuItem.children) {
			(menuItem as any).children = menuItem.children.filter((subItem) =>
				userRoles.some((userRole) => subItem.restrictedTo.includes(userRole as any)),
			);
			return true;
		}

		return allowed;
	});
};

// Computed
const imageAlt = computed(() => process.env.LOGO_ALT);
const image = computed(() => process.env.LOGO_IMAGE);
const isCollapse = computed(() => !isSidebarOpened.value);

const filteredMenuSlider = computed(() => {
	const copy = cloneDeep(menuItems.value);
	return copy.filter((item) => {
		if (item.badgeValue && (item as any)[item.badgeValue]) {
			item.badgeValue = (item as any)[item.badgeValue];
		} else if (item.badgeValue) {
			delete item.badgeValue;
		}

		if (item.hasOwnProperty('children')) {
			const filtered = item.children
				.filter((children: any) => {
					return children.label.toLowerCase().includes(search.value.toLowerCase());
				})
				.map((child: any) => {
					if (child.badgeValue && (child as any)[child.badgeValue]) {
						child.badgeValue = (child as any)[child.badgeValue];
					} else if (child.badgeValue) {
						delete child.badgeValue;
					}
					return child;
				});

			if (filtered.length) {
				item.children = filtered;
				return item;
			} else {
				return item.label.toLowerCase().includes(search.value.toLowerCase());
			}
		} else {
			return item.label.toLowerCase().includes(search.value.toLowerCase());
		}
	});
});

const hasBadge = computed(() => {
	return filteredMenuSlider.value.map(
		(x) => !!x.children && x.children.some((y: any) => y.badgeValue),
	);
});

/**
 * Controlla se una route è attiva (reattivo)
 * Logica migliorata per evitare match multipli con route annidate
 */
const isActiveRoute = computed(() => (routePath: string) => {
	const currentPath = route.path;

	// Match esatto ha sempre priorità
	if (currentPath === routePath) {
		return true;
	}

	// Per evitare match multipli, controlliamo se esiste una route più specifica
	// che potrebbe fare match con il path corrente
	const allRoutes = getAllSidebarRoutes();
	const moreSpecificRoutes = allRoutes.filter(
		(r) =>
			r !== routePath &&
			r.startsWith(routePath + '/') &&
			(currentPath === r || currentPath.startsWith(r + '/')),
	);

	// Se esiste una route più specifica che fa match, questa route non deve essere attiva
	if (moreSpecificRoutes.length > 0) {
		return false;
	}

	// Altrimenti usa la logica standard di startsWith
	return currentPath.startsWith(routePath + '/');
});

// Popup management
const showSubmenu = (itemId: string, event?: Event) => {
	if (isCollapse.value) {
		showingSubmenu.value = itemId;

		// Calcola la posizione Y del trigger
		if (event && event.currentTarget) {
			const target = event.currentTarget as HTMLElement;
			const rect = target.getBoundingClientRect();
			popupTop.value = rect.top;
		}
	}
};

const hideSubmenu = () => {
	if (isCollapse.value) {
		showingSubmenu.value = null;
	}
};

// Submenu toggle per versione expanded
const toggleSubmenuClick = (itemId: string) => {
	if (!isCollapse.value) {
		const index = openedMenus.value.indexOf(itemId);
		if (index === -1) {
			openedMenus.value.push(itemId);
		} else {
			openedMenus.value.splice(index, 1);
		}
	}
};

// Search handler
const searchHandler = () => {
	openedMenus.value = [];
	if (search.value.trim()) {
		menuItems.value.forEach((item, index) => {
			openedMenus.value.push(String(index));
		});
	}
};

const userInfoHandler = (value: any) => {
	if (value) {
		menuItems.value = getSidebarItems(value.roles);
	}
};

// Lifecycle
onMounted(() => {
	if (userInfo.value) {
		menuItems.value = getSidebarItems(userInfo.value.roles);
	}
	openedMenus.value.push('1');
});

// Methods
const onHamburgerClick = () => {
	appStore.toggleSidebar();
};

const onExpandInput = () => {
	appStore.toggleSidebar();
	nextTick(() => (textInput.value as any)?.focus());
};

// Watchers
watch(search, searchHandler);
watch(userInfo, userInfoHandler);

return { __sfc: true,appStore, authStore, route, userInfo, isSidebarOpened, search, openedMenus, menuItems, textInput, showingSubmenu, popupTop, getSidebarItems, imageAlt, image, isCollapse, filteredMenuSlider, hasBadge, isActiveRoute, showSubmenu, hideSubmenu, toggleSubmenuClick, searchHandler, userInfoHandler, onHamburgerClick, onExpandInput }
}

})